import { Injectable } from '@angular/core';

import { Workflow } from '@blaze-shared/workflow-domain';

interface WorkflowReferences {
  allTokens: Set<string>;
  firstNodeTokens: Set<string>;
}

const WF_INPUT_REFERENCE_REGEXP = /\$\$[A-Za-z0-9_]+/g;

@Injectable({ providedIn: 'root' })
export class WorkflowReferenceService {
  private readonly cache = new Map<string, WorkflowReferences>();

  usesInput(workflow: Workflow | null | undefined, token: string): boolean {
    if (!workflow) {
      return false;
    }
    return this.getWorkflowRereferences(workflow).allTokens.has(token);
  }

  firstNodeUsesInput(workflow: Workflow | null | undefined, token: string): boolean {
    if (!workflow) {
      return false;
    }
    return this.getWorkflowRereferences(workflow).firstNodeTokens.has(token);
  }

  private getCacheKey(workflow: Workflow): string {
    return `${workflow.id}-${workflow.revision}`;
  }

  private getWorkflowRereferences(workflow: Workflow): WorkflowReferences {
    const key = this.getCacheKey(workflow);
    const cachedWorkflowReferences = this.cache.get(key);
    if (cachedWorkflowReferences) {
      return cachedWorkflowReferences;
    }
    const workflowReferences = this.extractWorkflowReferences(workflow);
    this.cache.set(key, workflowReferences);
    return workflowReferences;
  }

  private extractWorkflowReferences(workflow: Workflow): WorkflowReferences {
    const allTokens = extractWorkflowTokens(JSON.stringify(workflow.nodes));
    const firstNodeTokens = extractWorkflowTokens(JSON.stringify(workflow.nodes?.[0]));
    return { allTokens, firstNodeTokens };
  }
}

function extractWorkflowTokens(serialized: string | undefined): Set<string> {
  if (!serialized) {
    return new Set();
  }
  const matches = serialized.match(WF_INPUT_REFERENCE_REGEXP);
  return new Set(matches ?? []);
}
